import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';

import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import countries from 'utils/countries.json';
import {
  AUSTRALIAN_STATES,
  CANADIAN_STATES,
  GERMAN_STATES,
  INDIAN_STATES,
  SINGAPORE_STATES,
  UAE_STATES,
  UNITED_KINGDOM_STATES,
  US_STATES,
} from 'utils/states';

const UPDATE_KYC_ADDRESS_MUTATION = gql(`
  mutation UpdateKycAddress(
    $id: ID!
    $lineOne: String!
    $lineTwo: String
    $city: String!
    $state: String!
    $country: String!
    $pincode: String!
  ) {
    updateKycAddress(
      id: $id
      lineOne: $lineOne
      lineTwo: $lineTwo
      city: $city
      state: $state
      country: $country
      pincode: $pincode
    ) {
      id
      address {
        lineOne
        lineTwo
        city
        state
        country
        pincode
      }
    }
  }
`);

const UpdateKycAddress: FC<{
  id: string;
  lineOne?: string | null;
  lineTwo?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
}> = ({ id, lineOne, lineTwo, city, state, country, pincode }) => {
  const [updateKycAddress, { loading, error }] = useMutation(UPDATE_KYC_ADDRESS_MUTATION);

  return (
    <FormPanelWithReadMode
      loading={loading}
      error={error}
      onSubmit={data => {
        updateKycAddress({
          variables: {
            id,
            lineOne: data.lineOne,
            lineTwo: data.lineTwo,
            city: data.city,
            state: data[data.country],
            country: data.country,
            pincode: data.pincode,
          },
        });
      }}
      title="Address"
    >
      <FormInput type="text" fieldName="lineOne" label="Line One" defaultValue={lineOne} />
      <FormInput type="text" fieldName="lineTwo" label="Line Two" defaultValue={lineTwo} />
      <FormInput type="text" fieldName="city" label="City" defaultValue={city} />
      <FormInput
        fieldName="country"
        type="select"
        label="Country"
        defaultValue={country || ''}
        options={Object.keys(countries).map(c => ({ label: countries[c], value: c }))}
        validators={{ required: true }}
      />
      <FormInput
        fieldName="IN"
        type="select"
        label="State"
        defaultValue={Object.keys(INDIAN_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(INDIAN_STATES).map(s => ({ label: INDIAN_STATES[s], value: s }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'IN' }] }}
      />
      <FormInput
        fieldName="US"
        type="select"
        label="State"
        defaultValue={Object.keys(US_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(US_STATES).map(s => ({ label: US_STATES[s], value: s }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'US' }] }}
      />
      <FormInput
        fieldName="GE"
        type="select"
        label="State"
        defaultValue={Object.keys(GERMAN_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(GERMAN_STATES).map(s => ({ label: GERMAN_STATES[s], value: s }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'GE' }] }}
      />
      <FormInput
        fieldName="SG"
        type="select"
        label="State"
        defaultValue={Object.keys(SINGAPORE_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(SINGAPORE_STATES).map(s => ({ label: SINGAPORE_STATES[s], value: s }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'SG' }] }}
      />
      <FormInput
        fieldName="UK"
        type="select"
        label="State"
        defaultValue={Object.keys(UNITED_KINGDOM_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(UNITED_KINGDOM_STATES).map(s => ({
          label: UNITED_KINGDOM_STATES[s],
          value: s,
        }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'UK' }] }}
      />
      <FormInput
        fieldName="AE"
        type="select"
        label="State"
        defaultValue={Object.keys(UAE_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(UAE_STATES).map(s => ({ label: UAE_STATES[s], value: s }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'AE' }] }}
      />
      <FormInput
        fieldName="AU"
        type="select"
        label="State"
        defaultValue={Object.keys(AUSTRALIAN_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(AUSTRALIAN_STATES).map(s => ({
          label: AUSTRALIAN_STATES[s],
          value: s,
        }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'AU' }] }}
      />
      <FormInput
        fieldName="CA"
        type="select"
        label="State"
        defaultValue={Object.keys(CANADIAN_STATES).some(s => s === state) ? state : ''}
        options={Object.keys(CANADIAN_STATES).map(s => ({ label: CANADIAN_STATES[s], value: s }))}
        validators={{ required: true }}
        conditionsToShow={{ matches: [{ field: 'country', condition: '===', value: 'CA' }] }}
      />
      <FormInput type="text" fieldName="pincode" label="Pincode" defaultValue={pincode} />
    </FormPanelWithReadMode>
  );
};

export default UpdateKycAddress;
